import { Box, Paper, styled } from '../../base';
import { Placement } from './SlidingDrawer.interfaces';

interface IWrapperProps {
    placement: Placement;
    positionOffset: string;
}

const getWrapperStyles = (placement: Placement, positionOffset: string) => {
    switch (placement) {
        case 'top':
            return {
                top: 0,
                left: positionOffset,
                transform: 'translateX(-50%)',
            };
        case 'bottom':
            return {
                bottom: 0,
                left: positionOffset,
                transform: 'translateX(-50%)',
            };
        case 'left':
            return {
                left: 0,
                top: positionOffset,
                transform: 'translateY(-50%)',
            };
        case 'right':
        default:
            return {
                right: 0,
                top: positionOffset,
                transform: 'translateY(-50%)',
            };
    }
};

export const DrawerWrapper = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== 'placement' && prop !== 'positionOffset',
})<IWrapperProps>(({ theme, placement, positionOffset }) => ({
    position: 'fixed',
    zIndex: theme.zIndex.drawer + 1,
    ...getWrapperStyles(placement, positionOffset),
}));

export const TabButton = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'placement',
})<{ placement: Placement }>(({ theme, placement }) => {
    const radius = theme.shape.borderRadius;
    let borderRadius = {};

    switch (placement) {
        case 'top':
            borderRadius = {
                borderTopLeftRadius: radius,
                borderTopRightRadius: radius,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
            };
            break;
        case 'bottom':
            borderRadius = {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomLeftRadius: radius,
                borderBottomRightRadius: radius,
            };
            break;
        case 'left':
            borderRadius = {
                borderTopLeftRadius: radius,
                borderBottomLeftRadius: radius,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
            };
            break;
        case 'right':
            borderRadius = {
                borderTopRightRadius: radius,
                borderBottomRightRadius: radius,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
            };
            break;
        default:
            borderRadius = { borderRadius: radius };
    }

    return {
        cursor: 'pointer',
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        opacity: 0.5,
        transition: theme.transitions.create('opacity'),
        '&:hover': {
            opacity: 1,
        },
        ...borderRadius,
        ...((placement === 'left' || placement === 'right') && {
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
        }),
    };
});

interface IDrawerContentProps {
    placement: Placement;
    isOpen: boolean;
}

const getClosedStateTransform = (placement: Placement) => {
    const styles = {
        top: { transform: 'translateY(-100%)' },
        bottom: { transform: 'translateY(100%)' },
        left: { transform: 'translateX(-100%)' },
        right: { transform: 'translateX(100%)' },
    };
    return styles[placement] || styles.right;
};

const getPositionStyles = (placement: Placement) => {
    switch (placement) {
        case 'top':
            return { top: 0, left: 0, right: 0 };
        case 'bottom':
            return { bottom: 0, left: 0, right: 0 };
        case 'left':
            return { left: 0, top: 0, bottom: 0 };
        case 'right':
        default:
            return { right: 0, top: 0, bottom: 0 };
    }
};

export const DrawerContent = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'placement' && prop !== 'isOpen',
})<IDrawerContentProps>(({ theme, placement, isOpen }) => ({
    position: 'fixed',
    zIndex: theme.zIndex.drawer,
    ...getPositionStyles(placement),
    width: placement === 'top' || placement === 'bottom' ? '100vw' : 'auto',
    height: placement === 'left' || placement === 'right' ? '100vh' : 'auto',
    transition: theme.transitions.create('transform', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    visibility: isOpen ? 'visible' : 'hidden',
    ...(!isOpen && getClosedStateTransform(placement)),
    ...(isOpen && { transform: 'translateX(0) translateY(0)' }),
}));
